import React from 'react'

import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { SvgIcon, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip } from '@scoir/tooltips'

import { getSegmentValue } from 'components/SegmentSelector/segment-service'

import Range from '../AcademicProfile/Range'
import { withContext } from '../Context'

export const SegmentName = 'MedianIncome'
const Operation = 'between'
const Min = 0
const Max = 250000
const MaxUpperBound = 1000000
const GranularStep = 5000
const DefaultStep = 50000

const formatDollarValue = (value: number): string => {
  if (value === 0) return '$0'
  if (value >= Max) return `$${value / 1000}k+`
  if (value >= 1000) return `$${value / 1000}k`
  return `$${value}`
}

const useStyles = makeStyles((theme) => ({
  header: {
    paddingTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  sliderContainer: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  infoIcon: {
    fontSize: '18px',
    alignSelf: 'center',
  },
}))

type MedianIncomeSelectorProps = {
  criteria: string
  onChange: (criteria: string, segmentName: string, value: any, operation: string) => void
  segment: object
  segmentName: string
  label: string
  isGranular?: boolean
}

export const MedianIncomeSelector = ({
  criteria = 'Must',
  onChange,
  segment,
  label,
  isGranular = false,
}: MedianIncomeSelectorProps) => {
  const step = isGranular ? GranularStep : DefaultStep
  const currentValue = getSegmentValue(segment, criteria, SegmentName)
  const [from, setFrom] = React.useState(currentValue[0] || Min)
  const [to, setTo] = React.useState(currentValue[1] || Max)
  const classes = useStyles()

  return (
    <>
      <div className={classes.header}>
        <div>
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="title"
          >
            Neighborhood Household Income
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            Min and max median income
          </Typography>
        </div>
        <Tooltip
          title="Based on median household income data"
          placement="top"
        >
          <SvgIcon
            color="primary"
            className={classes.infoIcon}
          >
            <FontAwesomeIcon
              icon={faInfoCircle}
              size="xs"
            />
          </SvgIcon>
        </Tooltip>
      </div>
      <div className={classes.sliderContainer}>
        <Range
          fixedLabelsWithMarks
          hideValueLabel
          allowOverlap
          step={step}
          min={Min}
          max={Max}
          from={from}
          to={to}
          getAriaValueText={(value: number) => `${label} range: ${formatDollarValue(value)}`}
          formatLabel={(value: number) => formatDollarValue(value)}
          onChange={(value: number[]): void => {
            const isDefaultValues = value[0] === Min && value[1] === Max
            const bothAtMin = value[0] === Min && value[1] === Min
            const maxValue = value[1] === Max ? MaxUpperBound : value[1]

            let values: number[]
            if (isDefaultValues) {
              values = []
            } else if (bothAtMin) {
              values = [Min, Min]
            } else {
              values = [value[0], maxValue]
            }

            onChange(criteria, SegmentName, values, Operation)

            setFrom(value[0])
            setTo(value[1])
          }}
        />
      </div>
    </>
  )
}

export default withContext(MedianIncomeSelector)
